import { computed, effect, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AW } from '@airwallet/types';
import { LocalizedKeys } from '@airwallet/translate/utils';
import dayjs from 'dayjs';
import { activeSupportTierTrial, isSupportSlaOffered, splitSupportTierProduct, supportHours, type SupportTier, type SupportWindow } from '@airwallet/data';
import { AuthService } from '@services/auth/auth.service';
import { HttpService } from '@services/helper/http.service';

export const supportTierNameKeys = {
    sla_tier_0: 'support-tier.sla_tier_0',
    sla_tier_1: 'support-tier.sla_tier_1',
    sla_tier_2: 'support-tier.sla_tier_2'
} as const satisfies Record<SupportTier, LocalizedKeys>;

export function formatSupportWindow({ openHour, closeHour }: SupportWindow): string {
    const hour = (value: number) => `${String(value % 24).padStart(2, '0')}:00`;
    return `${hour(openHour)}–${hour(closeHour)}`;
}

const supportTierRoles: ReadonlySet<AW.Account.Role> = new Set(['owner', 'editor']);

export interface SupportTierTrial {
    product: AW.SupportSla.PriceProduct;
    label: string;
    endUnix: number;
    end: string;
    cancelled: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SupportTierService {
    readonly isAvailable: Signal<boolean> = computed(() => {
        const owner = this.authService.dashboardOwnerUser();
        return !!owner && !owner.settings.is_regular && isSupportSlaOffered(this.authService.customerFirestore()) && this.authService.isBillingAllowed();
    });
    readonly canChoose: Signal<boolean> = computed(() => supportTierRoles.has(this.authService.getRole()));
    // Mirrors the route guard of account/support-tier: who may open the support tier page, and so act on a tier elsewhere
    readonly canManage: Signal<boolean> = computed(() => this.isAvailable() && this.canChoose());

    private readonly _info: WritableSignal<AW.SupportSla.SupportTierInfo | null> = signal(null);
    readonly info: Signal<AW.SupportSla.SupportTierInfo | null> = this._info.asReadonly();

    readonly supportTier: Signal<AW.SupportSla.PriceProduct | null> = computed(() => {
        const account = this.authService.customerFirestore();
        return account ? (account.support_tier ?? null) : (this._info()?.support_tier ?? null);
    });
    private readonly clock: WritableSignal<number> = signal(dayjs().unix());
    readonly trial: Signal<SupportTierTrial | null> = computed(() => {
        this.clock();
        const account = this.authService.customerFirestore();
        const product = account ? activeSupportTierTrial(account, dayjs().unix()) : null;
        if (!account || !product || !account.support_tier_trial_end) return null;
        const endUnix = account.support_tier_trial_end;
        return { product, label: this.tierLabel(product), endUnix, end: dayjs.unix(endUnix).format(dayjs.unix(endUnix).year() === dayjs().year() ? 'D MMMM' : 'D MMMM YYYY'), cancelled: !!account.support_tier_trial_cancelled_at };
    });
    readonly supportTierLabel: Signal<string | null> = computed(() => {
        const supportTier = this.supportTier();
        return supportTier ? this.tierLabel(supportTier) : null;
    });

    constructor(
        private authService: AuthService,
        private httpService: HttpService,
        private translate: TranslateService
    ) {
        effect(onCleanup => {
            const now = this.clock();
            const end = this.authService.customerFirestore()?.support_tier_trial_end;
            if (!end || end <= now) return;
            // setTimeout holds at most 2^31 - 1 ms; a later end is reached in steps
            const timer = setTimeout(() => this.clock.set(dayjs().unix()), Math.min((end - now + 1) * 1000, 2_147_483_647));
            onCleanup(() => clearTimeout(timer));
        });
    }

    private infoRequest: Promise<AW.SupportSla.SupportTierInfo> | null = null;
    private infoRequestCount = 0;

    // Callers share a request already out; `fresh` asks anew after a change, and only the newest answer is kept
    loadInfo(fresh = false): Promise<AW.SupportSla.SupportTierInfo> {
        if (this.infoRequest && !fresh) return this.infoRequest;
        const id = ++this.infoRequestCount;
        const request = this.httpService
            ._http<AW.SupportSla.SupportTierInfo>('billing', 'support-tier', 'get')
            .then(info => {
                if (id === this.infoRequestCount) this._info.set(info);
                return info;
            })
            .finally(() => {
                if (this.infoRequest === request) this.infoRequest = null;
            });
        this.infoRequest = request;
        return request;
    }

    async loadUpgradePreview(supportTier: AW.SupportSla.PriceProduct): Promise<AW.SupportSla.UpgradePreview> {
        const params: AW.SupportSla.UpgradePreviewParams = { support_tier: supportTier };
        return this.httpService._http<AW.SupportSla.UpgradePreview>('billing', 'support-tier/upgrade-preview', 'get', { params });
    }

    async upgradeSupportTier(params: AW.SupportSla.UpgradeSupportTierParams): Promise<AW.SupportSla.UpgradeSupportTierResult> {
        return this.httpService._http<AW.SupportSla.UpgradeSupportTierResult>('billing', 'support-tier/upgrade', 'post', { body: params });
    }

    async cancelTrial(): Promise<AW.SupportSla.CancelTrialResult> {
        return this.httpService._http<AW.SupportSla.CancelTrialResult>('billing', 'support-tier/trial/cancel', 'put', { body: {} });
    }

    async loadNewSubscriptionPreview(params: AW.SupportSla.NewSubscriptionPreviewParams): Promise<AW.SupportSla.NewSubscriptionPreview> {
        return this.httpService._http<AW.SupportSla.NewSubscriptionPreview>('billing', 'support-tier/subscription-quote', 'post', { body: params });
    }

    tierLabel(product: AW.SupportSla.PriceProduct): string {
        const { tier, phone } = splitSupportTierProduct(product);
        const name = this.translate.instant(supportTierNameKeys[tier]);
        return phone ? this.translate.instant('support-tier.with_phone', { tier: name }) : name;
    }

    hoursLabels(tier: SupportTier): string[] {
        const { weekday, weekend, holidays } = supportHours[tier];
        const labels: string[] = [];
        if (weekday) labels.push(this.translate.instant('support-tier.weekdays', { hours: formatSupportWindow(weekday) }));
        if (weekend) labels.push(this.translate.instant('support-tier.weekends', { hours: formatSupportWindow(weekend) }));
        if (holidays) labels.push(this.translate.instant('support-tier.public_holidays'));
        return labels;
    }

    hoursSummary(product: AW.SupportSla.PriceProduct): string {
        const { tier, phone } = splitSupportTierProduct(product);
        return [...this.hoursLabels(tier), this.translate.instant(phone ? 'support-tier.upgrade.chat_and_phone' : 'support-tier.upgrade.chat'), this.translate.instant('support-tier.ai_always')].join(' · ');
    }
}
